import React from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { getArticleBySlug, blogArticles } from "@/data/blog";
import { buildWhatsAppUrl } from "@/lib/site";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const BlogPost = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <main data-testid="blog-post-not-found" className="bg-cream text-ink min-h-[70vh] flex items-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-40 pb-24 text-center">
          <p className="overline">404</p>
          <h1 className="mt-4 font-serif text-5xl font-light">Artículo no encontrado</h1>
          <Link to="/blog" className="inline-flex items-center gap-2 mt-8 px-6 py-3 border border-ink text-xs tracking-[0.22em] uppercase btn-sweep sweep-ink hover:text-cream">
            <ArrowLeft size={14} /> {t.nav.blog}
          </Link>
        </div>
      </main>
    );
  }

  const others = blogArticles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <main data-testid="blog-post-page" className="bg-cream text-ink">
      {/* HERO */}
      <section className="pt-36 md:pt-44">
        <div className="max-w-[820px] mx-auto px-6 md:px-8">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 overline text-ink/60 hover:text-ink transition-colors">
            <ArrowLeft size={14} /> {t.nav.blog}
          </Link>
          <p className="overline mt-8 text-ink/60">{article.category} · {article.date}</p>
          <h1 className="mt-5 font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.02]">{article.title}</h1>
          <p className="mt-6 text-lg text-ink/70 font-light leading-relaxed">{article.excerpt}</p>
          <p className="mt-5 overline text-ink/45">{article.read}</p>
        </div>
      </section>

      {/* COVER */}
      <section className="mt-12">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8">
          <div className="aspect-[16/9] bg-ink overflow-hidden">
            <img src={article.img} alt={article.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* BODY */}
      <article className="py-16 md:py-20">
        <div className="max-w-[720px] mx-auto px-6 md:px-8">
          {article.body.map((block, i) => {
            if (block.t === "h") {
              return (
                <h2 key={i} className="font-serif text-3xl md:text-4xl font-light tracking-tight mt-12 mb-4 leading-tight">
                  {block.x}
                </h2>
              );
            }
            if (block.t === "ul") {
              return (
                <ul key={i} className="my-5 space-y-2.5">
                  {block.items.map((it, k) => (
                    <li key={k} className="flex items-start gap-3 text-ink/80 font-light leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 bg-ink/50 rounded-full shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={i} className="text-ink/80 font-light leading-[1.8] text-[17px] my-5">
                {block.x}
              </p>
            );
          })}

          {/* CTA */}
          <div className="mt-14 border-t border-ink/15 pt-10">
            <p className="font-serif text-3xl font-light leading-tight">{t.contact.title}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href={buildWhatsAppUrl("first_visit", lang)} target="_blank" rel="noopener noreferrer" data-testid="blog-post-whatsapp" className="px-6 py-3 bg-ink text-cream text-xs tracking-[0.22em] uppercase btn-sweep sweep-cream border border-ink hover:text-ink">
                {t.hero.cta_primary}
              </a>
              <Link to="/contacto" data-testid="blog-post-contact" className="px-6 py-3 border border-ink text-xs tracking-[0.22em] uppercase btn-sweep sweep-ink hover:text-cream">
                {t.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* MORE ARTICLES */}
      <section className="pb-28 border-t border-ink/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16">
          <p className="overline mb-10">{t.blog_page.eyebrow}</p>
          <div className="grid md:grid-cols-3 gap-10">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-more-${p.slug}`} className="group block">
                <div className="aspect-[4/3] bg-ink overflow-hidden mb-5">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <p className="overline text-ink/60">{p.category}</p>
                <h3 className="mt-2 font-serif text-2xl font-light leading-snug flex items-start gap-2 group-hover:underline underline-offset-4 decoration-1">
                  {p.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPost;
